'use client'

import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { useCallback, useSyncExternalStore, type CSSProperties, type MouseEvent } from 'react'

import {
  cheminPrecedent,
  cheminPrecedentServeur,
  sAbonnerAuChemin,
} from '@/lib/historique-navigation'

/**
 * Le retour de la page de certification.
 *
 * ⭐ DEUX COMPORTEMENTS SOUS UN SEUL GESTE. Si on arrive d'une page du site, on
 * remonte l'historique : la page précédente revient à l'endroit exact où elle
 * avait été quittée, position de défilement comprise, ce qu'un lien ne sait pas
 * faire. Si on est arrivé directement, par un lien de certificat partagé, on va
 * à l'accueil.
 *
 * ⛔ UN `history.back()` NU EST FAUX ICI. Cette page est faite pour être
 * atteignable directement, par quelqu'un d'extérieur qui vérifie un certificat.
 * Chez lui, remonter l'historique ne le ramène pas au site : il l'en SORT.
 *
 * ⛔ ET CE SONT DE VRAIS LIENS, PAS DES BOUTONS. Le repli est écrit dans le
 * `href` : sans JavaScript, avec un moteur d'indexation, ou pendant
 * l'hydratation, le lien vers l'accueil fonctionne déjà. Le geste intelligent ne
 * s'ajoute par-dessus qu'une fois qu'on sait qu'il est légitime. Un `<button>`
 * aurait laissé un cul-de-sac dans tous ces cas.
 *
 * ⚠️ Les clics modifiés ne sont JAMAIS interceptés : ctrl, cmd, maj, alt et le
 * bouton du milieu ouvrent l'accueil dans un onglet, comme sur n'importe quel
 * lien. Détourner ces clics est la faute la plus courante des retours faits à la
 * main, et elle casse une habitude que personne n'énonce.
 *
 * ⚠️ IMPLANTATION : monter `<SuiviNavigation />` dans le gabarit RACINE du
 * projet, pas dans celui du site. Il doit tourner sur `/certification` aussi,
 * qui vit hors du chrome, c'est-à-dire précisément là où on s'en sert.
 */

/**
 * Nomme un chemin, pour que le libellé dise où l'on retourne.
 *
 * ⚠️ INJECTÉ PAR LE PROJET, jamais lu ici. Le kit n'a aucun moyen de connaître
 * la navigation de l'hôte, et importer son module de navigation le rendrait
 * non implantable ailleurs. Sans fonction, le libellé reste générique, ce qui
 * est correct.
 */
export type NommerChemin = (chemin: string) => string | null

function usePrecedent() {
  return useSyncExternalStore(sAbonnerAuChemin, cheminPrecedent, cheminPrecedentServeur)
}

function useRetour() {
  const router = useRouter()

  return useCallback(
    (evenement: MouseEvent<HTMLAnchorElement>) => {
      if (evenement.defaultPrevented) return
      /* ⚠️ Laisser passer ce que l'utilisateur a demandé explicitement. */
      if (evenement.button !== 0) return
      if (evenement.metaKey || evenement.ctrlKey || evenement.shiftKey || evenement.altKey) return

      /*
       * ⛔ LU MAINTENANT, PAS AU MONTAGE. Au montage du bouton, le suivi du
       * gabarit racine n'a pas encore enregistré la page qu'on quittait :
       * l'effet des enfants passe avant celui des parents, donc décider au
       * montage remonte d'un cran de trop. Au clic, tout est en place.
       */
      if (!cheminPrecedent()) return /* le href vers l'accueil fait le travail */

      evenement.preventDefault()
      router.back()
    },
    [router],
  )
}

/**
 * Le bouton flottant, en bas à gauche.
 *
 * ⭐ À L'AUTRE BOUT DU MÊME AXE que le sommaire et le retour en haut, qui sont
 * en bas à droite et partagent une ligne horizontale alignée exprès. Le retour
 * garde cette ligne et n'encombre pas la paire.
 *
 * 🟢 Et c'est ce qui a écarté le coin haut gauche, sa place conventionnelle.
 * Mesuré le 2026-09-08 à 386 px de large : le bouton posé en (16, 16) sur 46 px
 * couvrait ENTIÈREMENT la marque de l'en-tête, à (27, 40) sur 26 × 22, parce
 * que la colonne de contenu n'a que `7vw` de marge à gauche. Aucun conflit à
 * largeur bureau, donc un défaut qui ne se voit que sur téléphone.
 *
 * ⚠️ En développement, le badge de Next occupe ce même coin et passe par-dessus.
 * Artefact de dev, absent en production.
 */
export function BoutonRetourFlottant({ nommer }: { nommer?: NommerChemin }) {
  const precedent = usePrecedent()
  const surClic = useRetour()

  const nom = precedent && nommer ? nommer(precedent) : null
  const libelle = precedent
    ? nom
      ? `Retour à ${nom}`
      : 'Retour à la page précédente'
    : 'Aller à l’accueil du site'

  return (
    <Link
      href="/"
      onClick={surClic}
      className="bouton-flottant retour-arriere"
      aria-label={libelle}
      title={libelle}
    >
      {/* ⚠️ La flèche est décorative : le libellé est porté par `aria-label`,
          sinon un lecteur d'écran annonce le caractère et rien d'autre.
          ⭐ Un TRACÉ et pas un caractère : 🟢 mesuré le 2026-09-10, l'encre du
          « ← » tombait 1,5 px hors du centre, et le SENS de l'écart suivait la
          fonte héritée. Un glyphe est centré par sa fonte, pas par la mise en
          page. Voir le bloc dédié dans `certificat.css`. */}
      <span aria-hidden>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75"
             strokeLinecap="round" strokeLinejoin="round">
          <path d="M19 12H5" />
          <path d="M11 6l-6 6 6 6" />
        </svg>
      </span>
    </Link>
  )
}

/**
 * La sortie en pied de certificat, « Revenir au site ».
 *
 * ⚠️ Le texte ne change PAS selon l'origine, contrairement au libellé du bouton
 * flottant : c'est une ligne de composition, dans une page où la typographie est
 * posée. Seul le geste s'adapte.
 */
export function LienRetourSite({
  classe = '',
  style,
}: {
  classe?: string
  style?: CSSProperties
}) {
  const surClic = useRetour()

  return (
    <Link href="/" onClick={surClic} className={classe} style={style}>
      Revenir au site
    </Link>
  )
}
